/**
 * Edit Technician Attributes dialog — update tier, clearance, location.
 * Admin-only.
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { X, Settings, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { GlassCard } from "@/components/sc/glass-card";
import { useUpdateTechnicianAttributes } from "@/hooks/use-technicians";
import { TechnicianAttributeForm } from "./technician-attribute-form";
import type { TechnicianSummary, TechnicianAttributeUpdate } from "@/types";

interface EditAttributesDialogProps {
  readonly open: boolean;
  readonly technician: TechnicianSummary | null;
  readonly onClose: () => void;
}

export function EditAttributesDialog({
  open,
  technician,
  onClose,
}: EditAttributesDialogProps) {
  const [attributes, setAttributes] = useState<TechnicianAttributeUpdate>({});
  const backdropRef = useRef<HTMLDivElement>(null);
  const updateMutation = useUpdateTechnicianAttributes();

  // Initialise form when technician changes
  useEffect(() => {
    if (technician) {
      setAttributes({
        technicianTier: technician.tier,
        securityClearance: technician.securityClearance,
        workLocationCountry: technician.workLocation,
      });
    }
  }, [technician]);

  const handleClose = useCallback(() => {
    updateMutation.reset();
    onClose();
  }, [onClose, updateMutation]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, handleClose]);

  const handleSubmit = useCallback(async () => {
    if (!technician) return;

    try {
      await updateMutation.mutateAsync({
        userId: technician.userId,
        attributes,
      });
      handleClose();
    } catch {
      // Error state handled by updateMutation.isError
    }
  }, [technician, attributes, updateMutation, handleClose]);

  if (!open || !technician) return null;

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === backdropRef.current) handleClose();
      }}
    >
      <GlassCard className="w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Settings size={20} className="text-sc-primary" />
            <h2 className="text-h3 font-display text-sc-navy">
              Edit Attributes
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="p-1 rounded-btn text-sc-text-muted hover:text-sc-text hover:bg-sc-surface-hover transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Technician info */}
        <div className="mb-4 p-3 rounded-input bg-sc-surface border border-sc-border">
          <p className="text-body-sm text-sc-text font-medium">
            {technician.displayName}
          </p>
          <p className="text-label text-sc-text-muted">
            {technician.userPrincipalName}
          </p>
        </div>

        <TechnicianAttributeForm
          attributes={attributes}
          onChange={setAttributes}
        />

        {updateMutation.isError && (
          <p className="text-label text-sc-danger mt-3">
            {(updateMutation.error as Error).message ?? "Failed to save"}
          </p>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" size="sm" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </div>
      </GlassCard>
    </div>
  );
}
